import { Component, Input, OnInit } from '@angular/core';
import { DataServiceService } from '../services/data-service.service';

@Component({
  selector: 'app-drivers-charts',
  templateUrl: './drivers-charts.component.html',
  styleUrl: './drivers-charts.component.scss'
})
export class DriversChartsComponent implements OnInit {

  constructor(private dataserviceService: DataServiceService) {
    this.selectedYear = this.year;
    this.selectedMonth = this.month;
  }

  @Input() getTotalOrdersForDay: ((date: Date) => number) | undefined;
  @Input() getDeliveredOrdersForDay: ((date: Date) => number) | undefined;
  @Input() driverId: number | undefined;
  @Input() lastSevenDays: Date[] = [];


  pickupChartData: any;
  pickupChartOptions: any;
  deliveryChartData: any;
  deliveryChartOptions: any;

  barChartData: any;

  today = new Date();
  year = this.today.getFullYear();
  month = this.today.getMonth() + 1;

  weeklyStats: any;
  selectedYear: number | undefined;
  selectedMonth: number | undefined;
  view: 'weekly' | 'monthly' = 'weekly';
  hasData: boolean = false;

  years: number[] = [];

  months = [
    { name: 'January', value: 1 },
    { name: 'February', value: 2 },
    { name: 'March', value: 3 },
    { name: 'April', value: 4 },
    { name: 'May', value: 5 },
    { name: 'June', value: 6 },
    { name: 'July', value: 7 },
    { name: 'August', value: 8 },
    { name: 'September', value: 9 },
    { name: 'October', value: 10 },
    { name: 'November', value: 11 },
    { name: 'December', value: 12 }
  ];


  ngOnInit() {
    this.loadData()
    this.dataserviceService.getUniqueYears().subscribe(years => {
      this.years = years;
    });
  }

  onDateChange() {
    this.loadData()
  }

  loadData() {
    if (this.view === 'weekly') {
      this.loadWeeklyData();
      // Load performance data for the selected week
      this.loadPerformanceData(this.selectedYear ?? 0, this.selectedMonth);
    } else {
      this.loadYearlyData();
      // Load performance data for the entire selected year
      this.loadPerformanceData(this.selectedYear ?? 0);
    }
  }

  loadPerformanceData(year: number, month?: number) {
    if (month) {
      this.dataserviceService.getWeeklyPerformance(
        this.driverId ?? 0,
        year,
        month
      ).subscribe(performance => {
        this.updateCharts(performance);
      });
    } else {
      this.dataserviceService.getYearlyPerformance(
        this.driverId ?? 0,
        year
      ).subscribe(performance => {
        this.updateCharts(performance);
      });
    }
  }


  loadWeeklyData() {
    this.dataserviceService.getWeeklyStats(
      this.driverId ?? 0,
      this.selectedYear ?? 0,
      this.selectedMonth ?? 0
    ).subscribe(data => {
      this.weeklyStats = data;
      this.hasData = data.datasets[0].data.some((value: number) => value > 0);
      if (this.hasData) {
        this.barChartData = {
          labels: this.weeklyStats.labels,
          datasets: [{
            label: 'Orders per Week',
            data: this.weeklyStats.datasets[0].data,
            backgroundColor: [
              'rgba(100, 116, 139, 0.2)',
            ],
            borderColor: [
              'rgb(100, 116, 139)'
            ],
            borderWidth: 1
          }]
        };
      }
    });
  }


  loadYearlyData() {
    this.dataserviceService.getMonthlyStats(
      this.driverId ?? 0,
      this.selectedYear ?? 0
    ).subscribe(data => {
      this.weeklyStats = data;
      this.hasData = data.datasets[0].data.some((value: number) => value > 0);
      if (this.hasData) {
        this.barChartData = {
          labels: this.weeklyStats.labels,
          datasets: [{
            label: 'Orders per Month',
            data: this.weeklyStats.datasets[0].data,
            backgroundColor: [
              'rgba(100, 116, 139, 0.2)',
            ],
            borderColor: [
              'rgb(100, 116, 139)'
            ],
            borderWidth: 1
          }]
        };
      }
    });
  }

  getSelectedPeriodText(): string {
    if (this.view === 'weekly') {
      const monthName = this.months.find(m => m.value === this.selectedMonth)?.name;
      return `${monthName} ${this.selectedYear}`;
    } else {
      return `${this.selectedYear}`;
    }
  }

  changeView(newView: 'weekly' | 'monthly') {
    this.view = newView;
    this.loadData();
  }


  barChartOptions = {
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            return `Loads: ${context.raw}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    },
    responsive: true,
    maintainAspectRatio: false
  };


  updateCharts(performance: any) {
    // Pickup Chart
    this.pickupChartData = {
      labels: ['On-Time Pickups', 'Late Pickups'],
      datasets: [{
        data: [
          performance.pickups.onTime,
          performance.pickups.late
        ],
        backgroundColor: [
          'rgba(16, 185, 129, 0.2)',  
          'rgba(244, 63, 94, 0.2)'    
        ],
        borderColor: [
          'rgb(16, 185, 129)',
          'rgb(244, 63, 94)'
        ],
        borderWidth: 1
      }]
    };

    // Delivery Chart
    this.deliveryChartData = {
      labels: ['On-Time Deliveries', 'Late Deliveries'],
      datasets: [{
        data: [
          performance.deliveries.onTime,
          performance.deliveries.late
        ],
        backgroundColor: [
          'rgba(16, 185, 129, 0.2)', 
          'rgba(244, 63, 94, 0.2)'    
        ],
        borderColor: [
          'rgb(16, 185, 129)',
          'rgb(244, 63, 94)'
        ],
        borderWidth: 1
      }]
    };

    // Shared chart options
    const chartOptions = {
      cutout: '50%',
      plugins: {
        tooltip: {
          callbacks: {
            label: function (context: any) {
              const label = context.label || '';
              const value = context.raw || 0;
              const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
              const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
              return `${label}: ${value} (${percentage}%)`;
            }
          }
        },
        legend: {
          position: 'bottom',
          display: true
        }
      },
      responsive: true,
      maintainAspectRatio: false
    };

    this.pickupChartOptions = chartOptions;
    this.deliveryChartOptions = chartOptions;
  }
}